import Label from '../../Utils/FormInputs/Label';
import TextField from '../../Utils/FormInputs/TextField';
/**
 * DayCaseDischargeChecklist.js
 *
 * Day-case (outpatient) discharge criteria checklist. All 10 items must be
 * checked before the patient can be discharged home. A progress bar tracks
 * completion. The last item (follow-up appointment) reveals a date picker
 * when checked.
 *
 * Props:
 *   surgeryId          – unique identifier for the current surgery
 *   onDischarge        – callback invoked after successful discharge
 *   onConvertToInpatient – callback to convert patient to inpatient admission
 */
import React, { useState, useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import LinearProgress from '@material-ui/core/LinearProgress';
import CircularProgress from '@material-ui/core/CircularProgress';
import Snackbar from '@material-ui/core/Snackbar';
import Alert from '@material-ui/lab/Alert';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import LocalHospitalIcon from '@material-ui/icons/LocalHospital';
import HomeIcon from '@material-ui/icons/Home';
import ChecklistItem from '../shared/ChecklistItem';
import { surgeryService } from '../../../services/surgeryService';

const useStyles = makeStyles(() => ({
  root: {
    padding: 24,
  },
  pageTitle: {
    fontSize: '1.35rem',
    fontWeight: 700,
    color: '#333',
    marginBottom: 4,
  },
  subtitle: {
    fontSize: '0.88rem',
    color: '#666',
    marginBottom: 24,
  },
  section: {
    padding: 20,
    marginBottom: 20,
    borderRadius: 8,
    border: '1px solid #e0e0e0',
  },
  sectionTitle: {
    fontSize: '1.05rem',
    fontWeight: 600,
    color: '#333',
    marginBottom: 16,
  },
  progressContainer: {
    marginBottom: 24,
  },
  progressLabel: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  progressCount: {
    fontSize: '0.92rem',
    fontWeight: 600,
    color: '#333',
  },
  progressPercent: {
    fontSize: '0.82rem',
    color: '#666',
  },
  progressBar: {
    height: 10,
    borderRadius: 5,
    backgroundColor: '#e0e0e0',
  },
  progressBarComplete: {
    backgroundColor: '#4CAF50',
  },
  progressBarPartial: {
    backgroundColor: '#FF9800',
  },
  followUpPicker: {
    marginLeft: 32,
    marginTop: 8,
    marginBottom: 8,
  },
  actionBtnRow: {
    display: 'flex',
    gap: 16,
    marginTop: 24,
  },
  dischargeBtn: {
    fontWeight: 600,
    backgroundColor: '#2E7D32',
    color: '#fff',
    '&:hover': { backgroundColor: '#1B5E20' },
    '&.Mui-disabled': {
      backgroundColor: '#C8E6C9',
      color: '#81C784',
    },
  },
  convertBtn: {
    fontWeight: 600,
    borderColor: '#D32F2F',
    color: '#D32F2F',
    '&:hover': { backgroundColor: '#FFEBEE' },
  },
  completeBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '14px 20px',
    backgroundColor: '#E8F5E9',
    borderRadius: 8,
    marginBottom: 20,
  },
  incompleteBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '14px 20px',
    backgroundColor: '#FFF3E0',
    borderRadius: 8,
    marginBottom: 20,
  },
}));

const CHECKLIST_ITEMS = [
  { key: 'stableVitals', label: 'Stable vital signs for at least 30 minutes' },
  { key: 'alertOriented', label: 'Alert and oriented (or back to baseline)' },
  { key: 'painControlled', label: 'Pain controlled with oral medications' },
  { key: 'noActiveBleeding', label: 'No active bleeding or drainage concerns' },
  { key: 'toleratedFluids', label: 'Tolerated oral fluids' },
  { key: 'ableToWalk', label: 'Able to walk (if applicable)' },
  { key: 'companionPresent', label: 'Responsible companion present' },
  { key: 'woundCareUnderstood', label: 'Patient understands wound care and warning signs' },
  { key: 'medicationsDispensed', label: 'Take-home medications dispensed and explained' },
  { key: 'followUpScheduled', label: 'Follow-up appointment scheduled' },
];

const DayCaseDischargeChecklist = ({ surgeryId, onDischarge, onConvertToInpatient }) => {
  const classes = useStyles();

  const [checkedItems, setCheckedItems] = useState(() => {
    const initial = {};
    CHECKLIST_ITEMS.forEach((item) => { initial[item.key] = false; });
    return initial;
  });
  const [followUpDate, setFollowUpDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const completedCount = useMemo(() => {
    return Object.values(checkedItems).filter(Boolean).length;
  }, [checkedItems]);

  const totalItems = CHECKLIST_ITEMS.length;
  const allComplete = completedCount === totalItems;
  const progressPercent = Math.round((completedCount / totalItems) * 100);

  const handleToggle = (key) => () => {
    setCheckedItems((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleDischarge = async () => {
    if (!allComplete) return;
    setLoading(true);
    try {
      const res = await surgeryService.dischargeDayCase({
        surgery_id: surgeryId,
        checklist: checkedItems,
        follow_up_date: followUpDate,
        discharge_time: new Date().toISOString(),
      });
      const data = res.data || res;
      showSnackbar(data.message || 'Patient discharged successfully');
      if (onDischarge) {
        onDischarge(data);
      }
    } catch (err) {
      showSnackbar(err.message || 'Failed to discharge patient', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleConvertToInpatient = () => {
    if (onConvertToInpatient) {
      onConvertToInpatient();
    }
  };

  return (
    <div className={classes.root}>
      <Typography className={classes.pageTitle}>Day-Case Discharge Checklist</Typography>
      <Typography className={classes.subtitle}>
        All criteria must be met before the patient can be discharged home &mdash; Surgery #{surgeryId}
      </Typography>

      {/* Progress Bar */}
      <div className={classes.progressContainer}>
        <div className={classes.progressLabel}>
          <Typography className={classes.progressCount}>
            {completedCount} of {totalItems} items complete
          </Typography>
          <Typography className={classes.progressPercent}>
            {progressPercent}%
          </Typography>
        </div>
        <LinearProgress
          variant="determinate"
          value={progressPercent}
          className={classes.progressBar}
          classes={{
            bar: allComplete ? classes.progressBarComplete : classes.progressBarPartial,
          }}
        />
      </div>

      {/* Status Banner */}
      {allComplete ? (
        <div className={classes.completeBanner}>
          <CheckCircleIcon style={{ color: '#2E7D32' }} />
          <Typography style={{ fontSize: '0.92rem', fontWeight: 600, color: '#2E7D32' }}>
            All discharge criteria met — patient is ready for discharge
          </Typography>
        </div>
      ) : (
        <div className={classes.incompleteBanner}>
          <Typography style={{ fontSize: '0.92rem', fontWeight: 600, color: '#E65100' }}>
            {totalItems - completedCount} item{totalItems - completedCount !== 1 ? 's' : ''} remaining
          </Typography>
        </div>
      )}

      {/* Checklist Items */}
      <Paper className={classes.section} elevation={0}>
        <Typography className={classes.sectionTitle}>Discharge Criteria</Typography>

        {CHECKLIST_ITEMS.map((item) => (
          <div key={item.key}>
            <ChecklistItem
              label={item.label}
              checked={checkedItems[item.key]}
              onChange={handleToggle(item.key)}
              required
              disabled={loading}
            />
            {/* Follow-up date picker when last item is checked */}
            {item.key === 'followUpScheduled' && checkedItems.followUpScheduled && (
              <div className={classes.followUpPicker}>
                <TextField
                  variant="outlined"
                  size="small"
                  label="Follow-up Appointment Date"
                  type="date"
                  value={followUpDate}
                  onChange={(e) => setFollowUpDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  style={{ minWidth: 240 }}
                  disabled={loading}
                  placeholder="e.g. Dr. Ssekitoleko's clinic"
                />
              </div>
            )}
          </div>
        ))}
      </Paper>

      {/* Action Buttons */}
      <div className={classes.actionBtnRow}>
        <Button
          variant="contained"
          className={classes.dischargeBtn}
          startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <HomeIcon />}
          onClick={handleDischarge}
          disabled={!allComplete || loading}
          size="large"
        >
          Discharge Home
        </Button>
        <Button
          variant="outlined"
          className={classes.convertBtn}
          startIcon={<LocalHospitalIcon />}
          onClick={handleConvertToInpatient}
          disabled={loading}
          size="large"
        >
          Convert to Inpatient
        </Button>
      </div>

      {!allComplete && (
        <Typography style={{ fontSize: '0.82rem', color: '#888', marginTop: 12 }}>
          All {totalItems} items must be checked before the patient can be discharged.
        </Typography>
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          elevation={6}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default DayCaseDischargeChecklist;
